import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useFlutterwave, closePaymentModal } from "flutterwave-react-v3";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import * as ClientAction from "../../store/redux/client_reducer.js";
import { serverHost } from "../../http_common.js";
import useSubscriptionAccess from "../../authorization/hooks/useSubscriptionAccess.js";
import "./planBilled.css";

const number = (value) => {
  const parsed = Number(value?.$numberDecimal ?? value);
  return Number.isFinite(parsed) ? parsed : 0;
};
const currency = (amount) =>
  `₦${amount.toLocaleString("en-NG", { maximumFractionDigits: 2 })}`;
const planFeatures = (plan) => {
  const values = plan?.features || plan?.capabilities || plan?.includedFeatures;
  return Array.isArray(values)
    ? values
        .map((value) =>
          typeof value === "string" ? value : value?.name || value?.description,
        )
        .filter(Boolean)
    : [];
};
// Display-only helpers (checkout logic below is unchanged).
const periodEnd = (cycle) => {
  const d = new Date();
  if (cycle === "YEARLY") d.setFullYear(d.getFullYear() + 1);
  else d.setMonth(d.getMonth() + 1);
  return d;
};
const formatDate = (date) => {
  const d = new Date(date);
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleDateString("en-NG", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
};
const yearlySaving = (plan) => {
  const monthly = number(plan?.pricing?.monthly),
    yearly = number(plan?.pricing?.yearly);
  return monthly > 0 && yearly > 0 && yearly < monthly * 12
    ? Math.round((1 - yearly / (monthly * 12)) * 100)
    : 0;
};
const Tick = () => (
  <svg
    className="uniBillingTick"
    viewBox="0 0 16 16"
    width="16"
    height="16"
    aria-hidden="true"
  >
    <path
      d="M3.5 8.5l3 3 6-7"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export default function PlansBilled({ onClose = () => {} }) {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const company = useSelector(
    (state) => state.clientFunction?.companiesDB?.clientData,
  );
  const accessType = useSelector((state) => state.clientFunction?.accessType);
  const { active, state } = useSubscriptionAccess();
  const [plans, setPlans] = useState([]);
  const [selected, setSelected] = useState(null);
  const [billingCycle, setBillingCycle] = useState(
    company?.subscription?.billingCycle === "YEARLY" ? "YEARLY" : "MONTHLY",
  );
  const [showAll, setShowAll] = useState(false);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [checkoutAttempt, setCheckoutAttempt] = useState(0);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const checkoutRef = useRef(null);
  const requestRef = useRef(false);
  const callbackRunningRef = useRef(false);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    let alive = true;
    setLoading(true);
    axios
      .get(`${serverHost}/company_features/get_plans`)
      .then((response) => {
        if (!alive) return;
        const rows = Array.isArray(response.data?.companyPlans)
          ? response.data.companyPlans
          : [];
        setPlans(rows);
        const requested = searchParams.get("plan");
        const index = Number(searchParams.get("index"));
        const preferred =
          rows.find((item) => item.slug === requested) ||
          (Number.isInteger(index) &&
          index >= 0 &&
          index < rows.length &&
          requested
            ? rows[index]
            : null) ||
          rows.find(
            (item) =>
              item.planId === company?.subscription?.planId ||
              item.name?.toLowerCase() ===
                company?.subscription?.planName?.toLowerCase(),
          ) ||
          rows[0];
        setSelected(
          preferred?.planId || preferred?.slug || preferred?._id || null,
        );
        if (!rows.length)
          setError(
            "Subscription plans are unavailable right now. Please retry later.",
          );
      })
      .catch(() => {
        if (alive)
          setError(
            "Unable to load subscription plans. Check your connection and try again.",
          );
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [company?.clientId]); // Do not reload plans when the URL changes while selecting a card.

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape" && !paying) closeRef.current();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [paying]);

  const chosen =
    plans.find((item) => (item.planId || item.slug || item._id) === selected) ||
    null;
  const price = chosen
    ? number(
        billingCycle === "YEARLY"
          ? chosen.pricing?.yearly
          : chosen.pricing?.monthly,
      )
    : 0;
  const publicKey = "FLWPUBK-c471ed8a7c4f5cd4691bb6023e513fba-X";
  const email = company?.founder?.email || "";
  const checkoutConfig = useMemo(
    () => ({
      public_key: "FLWPUBK-c471ed8a7c4f5cd4691bb6023e513fba-X",
      tx_ref: `universe-sub-${company?.clientId || "company"}-${Date.now()}-${checkoutAttempt}`,
      amount: price,
      currency: "NGN",
      payment_options: "card,mobilemoney,ussd",
      customer: {
        email,
        phone_number: company?.founder?.phone || "",
        name: company?.founder?.firstName || "Business owner",
      },
      customizations: {
        title: "Universe Inventory",
        description: "Company subscription renewal",
        logo: "",
      },
    }),
    [
      publicKey,
      price,
      email,
      company?.clientId,
      company?.founder?.phone,
      company?.founder?.firstName,
      selected,
      billingCycle,
      checkoutAttempt,
    ],
  );
  const flutterwave = useFlutterwave(checkoutConfig);

  async function beginCheckout() {
    if (
      requestRef.current ||
      !chosen ||
      !(price > 0) ||
      !publicKey ||
      !company?.clientId ||
      !email ||
      accessType !== "CLIENT_ACCESS"
    ) {
      setError(
        "Checkout is not ready. Check your plan, company email and payment configuration.",
      );
      return;
    }
    setError("");
    requestRef.current = true;
    setPaying(true);
    const selectedAtCheckout = chosen;
    const cycleAtCheckout = billingCycle;
    const timestamp = new Date();
    const until = new Date(timestamp);
    if (cycleAtCheckout === "YEARLY")
      until.setFullYear(until.getFullYear() + 1);
    else until.setMonth(until.getMonth() + 1);
    const grace = new Date(until);
    grace.setDate(grace.getDate() + 5);
    const subscription = {
      planId: selectedAtCheckout.planId || "",
      planName: selectedAtCheckout.name || "",
      status: "ACTIVE",
      billingCycle: cycleAtCheckout,
      startDate: timestamp.toISOString(),
      expiryDate: until.toISOString(),
      gracePeriod: grace.toISOString(),
      autoRenew: false,
      paymentMethod: "PAYMENT_GETWAY",
      lastPaymentDate: timestamp.toISOString(),
    };
    checkoutRef.current = subscription;
    try {
      flutterwave({
        callback: async (response) => {
          callbackRunningRef.current = true;
          try {
            if (!["successful", "completed"].includes(response?.status)) {
              setError(
                "Payment was not completed. Your subscription has not been changed.",
              );
              return;
            }
            // Existing backend contract. Independent payment verification is NOT provided by this API.
            // Never update Redux merely because the browser payment callback reports success.
            const result = await axios.put(
              `${serverHost}/company-billing/subscription/subscribe-company`,
              {
                clientId: company.clientId,
                subscription: checkoutRef.current,
              },
              { timeout: 30000 },
            );
            if (result.data?.status !== 200 && result.data?.status !== 201) {
              throw new Error(
                result.data?.message ||
                  "Your payment was reported, but the subscription could not be updated.",
              );
            }
            dispatch(
              ClientAction.updateCompanySubscriptionSnapshot({
                clientId: company.clientId,
                subscription: checkoutRef.current,
              }),
            );
            setSuccess(true);
            setError("");
          } catch (err) {
            setError(
              err.response?.data?.message ||
                err.message ||
                "Subscription update failed. Please contact support with your payment reference.",
            );
          } finally {
            setPaying(false);
            requestRef.current = false;
            callbackRunningRef.current = false;
            setCheckoutAttempt((value) => value + 1);
            closePaymentModal();
          }
        },
        onClose: () => {
          if (!callbackRunningRef.current && requestRef.current) {
            requestRef.current = false;
            setPaying(false);
            setCheckoutAttempt((value) => value + 1);
          }
        },
      });
    } catch (err) {
      setError(err.message || "Checkout could not start. Please try again.");
      requestRef.current = false;
      setPaying(false);
      setCheckoutAttempt((value) => value + 1);
    }
  }

  const currentPlanId = company?.subscription?.planId;
  const currentExpiry = formatDate(company?.subscription?.expiryDate);
  const canPay = !(
    paying ||
    loading ||
    !chosen ||
    price <= 0 ||
    !publicKey ||
    accessType !== "CLIENT_ACCESS"
  );
  const cycleWord = billingCycle === "YEARLY" ? "year" : "month";
  const saving = yearlySaving(chosen);
  const statusLabel =
    state === "expired" ? "Expired" : active ? "Active" : "Inactive";
  const visiblePlans = showAll ? plans : [chosen].filter(Boolean);

  const renderPlan = (plan) => {
    const planKey = plan.planId || plan.slug || plan._id;
    const amount = number(
      billingCycle === "YEARLY" ? plan.pricing?.yearly : plan.pricing?.monthly,
    );
    const isSelected = planKey === selected;
    const features = planFeatures(plan).slice(0, 6);
    return (
      <button
        type="button"
        key={planKey}
        onClick={() => setSelected(planKey)}
        className={`uniBillingPlanCard ${isSelected ? "chosen" : ""}`}
        disabled={paying}
        aria-pressed={isSelected}
      >
        <span className="uniBillingPlanTop">
          <strong>{plan.name || "Subscription plan"}</strong>
          <span className="uniBillingTags">
            {planKey === currentPlanId && (
              <span className="uniBillingTag uniBillingTag--current">
                Current plan
              </span>
            )}
            {isSelected && (
              <span className="uniBillingTag uniBillingTag--selected">
                Selected
              </span>
            )}
          </span>
        </span>
        <span className="uniBillingPrice">
          {currency(amount)}
          <small>/{cycleWord}</small>
        </span>
        <span className="uniBillingPlanDescription">
          {plan.description ||
            "Access features available with this subscription tier."}
        </span>
        <span className="uniBillingFeatures">
          <span className="uniBillingPlanFeature">
            <Tick />
            Company workspace access according to your plan
          </span>
          <span className="uniBillingPlanFeature">
            <Tick />
            Assigned staff permissions continue to apply
          </span>
          {features.map((feature, i) => (
            <span className="uniBillingPlanFeature" key={`${i}-${feature}`}>
              <Tick />
              {feature}
            </span>
          ))}
        </span>
      </button>
    );
  };

  return (
    <div
      className={`uniBillingModal ${showAll ? "uniBillingModal--allPlans" : "uniBillingModal--selected"}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="uniBillingTitle"
    >
      <button
        type="button"
        className="uniBillingClose"
        aria-label="Close subscription window"
        onClick={onClose}
        disabled={paying}
      >
        <CloseIcon fontSize="small" />
      </button>

      {success ? (
        <div className="uniBillingSuccess">
          <span className="uniBillingSuccessMark" aria-hidden="true">
            <Tick />
          </span>
          <h2 id="uniBillingTitle">Subscription updated</h2>
          <p>
            Your {chosen?.name || "new"} plan is active until{" "}
            {formatDate(checkoutRef.current?.expiryDate)}.
          </p>
          <button
            type="button"
            className="uniBillingCheckout"
            onClick={onClose}
          >
            Continue to Universe
          </button>
        </div>
      ) : (
        <>
          <header className="uniBillingHeader">
            <div className="uniBillingBrand">
              <span className="uniBillingLogo" aria-hidden="true" />
              Universe Inventory billing
            </div>
            <h2 id="uniBillingTitle">
              {active ? "Choose your plan" : "Restore access to your business"}
            </h2>
            <p>
              {state === "expired"
                ? "Your subscription has expired. Pick a plan to get your team back to work."
                : "Pick a plan and billing period. You can change either before you pay."}
            </p>
            {company?.subscription?.planName && (
              <div
                className={`uniBillingStatusChip uniBillingStatusChip--${state === "expired" ? "expired" : active ? "active" : "idle"}`}
              >
                <span className="dot" aria-hidden="true" />
                {statusLabel} · {company.subscription.planName}
                {currentExpiry ? ` · ends ${currentExpiry}` : ""}
              </div>
            )}
          </header>

          <div className="uniBillingBody">
            <section className="uniBillingMain" aria-label="Plans">
              <div className="uniBillingToolbar">
                <div
                  className="uniBillingCycle"
                  role="group"
                  aria-label="Billing period"
                >
                  <button
                    type="button"
                    className={billingCycle === "MONTHLY" ? "selected" : ""}
                    onClick={() => setBillingCycle("MONTHLY")}
                    disabled={paying}
                    aria-pressed={billingCycle === "MONTHLY"}
                  >
                    Monthly
                  </button>
                  <button
                    type="button"
                    className={billingCycle === "YEARLY" ? "selected" : ""}
                    onClick={() => setBillingCycle("YEARLY")}
                    disabled={paying}
                    aria-pressed={billingCycle === "YEARLY"}
                  >
                    Annually{saving > 0 && <em>Save {saving}%</em>}
                  </button>
                </div>
                {plans.length > 1 && (
                  <button
                    type="button"
                    className="uniBillingAllPlans"
                    onClick={() => setShowAll((v) => !v)}
                  >
                    {showAll
                      ? "Show selected plan only"
                      : `Compare all ${plans.length} plans`}
                  </button>
                )}
              </div>

              {loading ? (
                <div
                  className="uniBillingSkeleton"
                  role="status"
                  aria-label="Loading available plans"
                >
                  <span />
                  <span />
                  <span />
                </div>
              ) : plans.length ? (
                <div className="uniBillingCards">
                  {visiblePlans.map(renderPlan)}
                </div>
              ) : (
                <div className="uniBillingEmpty" role="alert">
                  <p>{error || "There are no available plans."}</p>
                  <button
                    type="button"
                    className="uniBillingAllPlans"
                    onClick={() => window.location.reload()}
                  >
                    Try again
                  </button>
                </div>
              )}
            </section>

            <aside className="uniBillingSummary" aria-label="Order summary">
              <h3>Order summary</h3>
              <dl>
                <div>
                  <dt>Plan</dt>
                  <dd>{chosen?.name || "Not selected"}</dd>
                </div>
                <div>
                  <dt>Billing</dt>
                  <dd>{billingCycle === "YEARLY" ? "Annual" : "Monthly"}</dd>
                </div>
                <div>
                  <dt>Access until</dt>
                  <dd>{chosen ? formatDate(periodEnd(billingCycle)) : "—"}</dd>
                </div>
                <div>
                  <dt>Renewal</dt>
                  <dd>Manual, no auto-charge</dd>
                </div>
              </dl>
              <div className="uniBillingTotal">
                <span>Total today</span>
                <strong>{chosen ? currency(price) : "—"}</strong>
              </div>
              {error && plans.length > 0 && (
                <p className="uniBillingError" role="alert">
                  {error}
                </p>
              )}
              <button
                type="button"
                className="uniBillingCheckout"
                disabled={!canPay}
                onClick={beginCheckout}
              >
                {paying
                  ? "Processing payment…"
                  : chosen
                    ? `Pay ${currency(price)}`
                    : "Choose a plan"}
              </button>
              <small className="uniBillingSecure">
                Secure checkout by Flutterwave. Pay by card, mobile money or
                USSD.
              </small>
              <button
                type="button"
                className="uniBillingLater"
                onClick={onClose}
                disabled={paying}
              >
                Not now
              </button>
            </aside>
          </div>
        </>
      )}
    </div>
  );
}
